export type NestedEntry<Entry> = { children?: readonly Entry[]; key: string };

export type NavRow<Entry> = { depth: number; entry: Entry; hasChildren: boolean };

/** Past this depth a row stops indenting further, so a deep tree keeps a readable label width. */
export const MAX_INDENT_DEPTH = 4;

/**
 * The rows a rail draws for a nested entry list, given which keys are open.
 *
 * A branch is walked only when its own key is open, so a key left in the open
 * set for a row whose parent has since closed contributes nothing.
 *
 * `'all'` walks every branch. A rail that passes nested entries without saying
 * how a row folds uses it, so its rows are all visible rather than silently
 * missing, which is how a group behaves without `onToggleGroup`.
 */
export const navRows = <Entry extends NestedEntry<Entry>>(entries: readonly Entry[], open: ReadonlySet<string> | 'all', depth = 0): NavRow<Entry>[] =>
  entries.flatMap(entry => {
    const children = entry.children ?? [];
    const row: NavRow<Entry> = { depth, entry, hasChildren: children.length > 0 };
    const walk = open === 'all' || open.has(entry.key);
    return walk && children.length > 0 ? [row, ...navRows(children, open, depth + 1)] : [row];
  });

export const entryKeys = <Entry extends NestedEntry<Entry>>(entries: readonly Entry[]): string[] => entries.flatMap(entry => [entry.key, ...entryKeys(entry.children ?? [])]);
